import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Modal } from 'react-bootstrap';
import { Blocks } from 'react-loader-spinner';
import { saveAs } from 'file-saver';
import MyPage from '../../MyPage';
import SubTabsWarehouses from '../../SubTabsWarehouses';
import { isSuperAdmin } from '../../helpers/user';
import axiosService from '../../helpers/axios';

const API_BASE = '/v1/dictionary-partnumbers-sapcodes';
const API_DOWNLOAD = '/v1/dictionary-partnumbers-sapcodes-download/';
const API_UPLOAD = '/v1/dictionary-partnumbers-sapcodes-upload/';

const EMPTY_FORM = { id: null, sapcode: '', part_number: '' };

function unwrapV1(res) {
  const body = res?.data;
  if (body && typeof body === 'object' && 'data' in body && ('status' in body || 'message' in body)) {
    return body.data;
  }
  return body;
}

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (data?.results) return data.results;
  return [];
}

function formatError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Request failed';
  if (typeof data === 'string') return data;
  const unwrapped = data?.data && typeof data.data === 'object' ? data.data : data;
  if (unwrapped.error) return unwrapped.error;
  if (unwrapped.message) return unwrapped.message;
  if (unwrapped.detail) return unwrapped.detail;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  const fieldErrors = Object.entries(unwrapped)
    .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
    .join('\n');
  return fieldErrors || 'Request failed';
}

function OptionsCell({ data, onEdit, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-2" style={{ height: '100%' }}>
      <button type="button" className="btn btn-light btn-sm p-1" title="Edit" onClick={() => onEdit(data)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
          <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
        </svg>
      </button>
      <button type="button" className="btn btn-light btn-sm p-1" title="Delete" onClick={() => onDelete(data)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
        </svg>
      </button>
    </div>
  );
}

export default function WarehousesDictionaryPartnumbersSapcodes() {
  const canEdit = isSuperAdmin();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosService.get(`${API_BASE}/`);
      setRowData(unwrapList(unwrapV1(res)));
    } catch (err) {
      console.error('Error fetching dictionary-partnumbers-sapcodes:', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      sapcode: row.sapcode ?? '',
      part_number: row.part_number ?? '',
    });
    setIsEdit(true);
    setShowModal(true);
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete mapping for part number "${row.part_number}"?`)) return;
    axiosService
      .delete(`${API_BASE}/${row.id}/`)
      .then(() => {
        alert('Delete successful');
        fetchList();
      })
      .catch((err) => alert(formatError(err)));
  };

  const validateForm = () => {
    if (!form.sapcode?.trim()) {
      alert('SAP code is required');
      return false;
    }
    if (!form.part_number?.trim()) {
      alert('Part number is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      sapcode: form.sapcode.trim(),
      part_number: form.part_number.trim(),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await axiosService.patch(`${API_BASE}/${form.id}/`, payload);
        alert('Update successful');
      } else {
        await axiosService.post(`${API_BASE}/`, payload);
        alert('Entry created');
      }
      setShowModal(false);
      fetchList();
    } catch (err) {
      alert(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axiosService.get(API_DOWNLOAD, { responseType: 'blob' });
      saveAs(res.data, 'dictionary_partnumbers_sapcodes.csv');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      alert('Failed to download CSV file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a CSV file to upload.');
      return;
    }
    if (
      !window.confirm(
        'This will delete ALL existing dictionary rows and replace them with the uploaded file.\n\nContinue?'
      )
    ) {
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await axiosService.post(API_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = unwrapV1(res);
      alert(result?.message || 'Import completed.');
      setFile(null);
      fetchList();
    } catch (err) {
      alert(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const columnDefs = useMemo(() => {
    const cols = [
      { field: 'id', headerName: 'ID', width: 90, filter: 'agNumberColumnFilter' },
      { field: 'sapcode', headerName: 'SAP Code', minWidth: 160, flex: 1 },
      { field: 'part_number', headerName: 'Part Number', minWidth: 180, flex: 1 },
    ];
    if (canEdit) {
      cols.push({
        headerName: 'Options',
        width: 120,
        pinned: 'right',
        filter: false,
        sortable: false,
        cellRenderer: OptionsCell,
        cellRendererParams: { onEdit: openEdit, onDelete: handleDelete },
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
    }),
    []
  );

  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsWarehouses />

      <div style={{ padding: '10px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>
          Dictionary — Part Numbers / SAP Codes
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
          Maps LEO part numbers to SAP codes used in inventory imports.
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          {canEdit && (
            <button type="button" className="btn btn-success btn-sm" onClick={openAdd}>
              Add entry
            </button>
          )}
          <input
            className="form-control form-control-sm"
            style={{ width: '220px' }}
            placeholder="Search in grid..."
            value={quickFilterText}
            onChange={(e) => setQuickFilterText(e.target.value)}
          />
          <div className="ms-auto d-flex flex-wrap align-items-center gap-2">
            {canEdit && (
              <>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  style={{ width: '220px' }}
                  accept=".csv,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleUpload}
                  disabled={uploading || !file}
                >
                  {uploading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : 'Download CSV'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchList}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        {canEdit && (
          <div style={{ fontSize: '11px', color: '#b45309', marginBottom: '8px' }}>
            Upload replaces the entire table (delete all, then import). Use Download to export the current data first.
          </div>
        )}

        {loading ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '50vh' }}>
            <Blocks visible height="100" width="100" ariaLabel="loading" />
            <div>Loading...</div>
          </div>
        ) : (
          <div className="ag-theme-quartz" style={{ height: '70vh', width: '100%' }}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={quickFilterText}
              animateRows
              getRowId={(p) => String(p.data?.id ?? '')}
            />
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '14px' }}>
            {isEdit ? 'Edit mapping' : 'Add mapping'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isEdit && (
            <div className="input-group input-group-sm mb-2">
              <span className="input-group-text" style={{ width: '110px' }}>ID</span>
              <input className="form-control" value={form.id} readOnly disabled />
            </div>
          )}
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text" style={{ width: '110px' }}>SAP Code</span>
            <input
              className="form-control"
              value={form.sapcode}
              onChange={(e) => setForm((prev) => ({ ...prev, sapcode: e.target.value }))}
            />
          </div>
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text" style={{ width: '110px' }}>Part Number</span>
            <input
              className="form-control"
              value={form.part_number}
              onChange={(e) => setForm((prev) => ({ ...prev, part_number: e.target.value }))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
